import React, { useCallback } from 'react';
import { Button, Tooltip, message } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import SingleSelect from 'components/SingleSelect';
import { t } from 'i18n';

export interface TransitInfoRow {
    key: string;
    transitWarehouseId: string;      // 转运站（关联warehouse）
    transitDestWarehouseId: string;  // 转运目的地站（关联warehouse）
    /** 转运目的地站已有装车数据 → 锁定行：目的地站不可改/删，转运站仅可改为未发车站（以后端保存校验为准） */
    destLoaded?: boolean;
    /** 转运货物已卸车 → 冻结行：目的地站与转运站均不可改/删（消费完毕，以后端保存校验为准） */
    destUnloaded?: boolean;
}

export interface TransitOption {
    label: string;
    value: string | number;
    /** 禁选（如已发车站）；SingleSelect 会置灰不可选，但仍在列表内保证 label 正常显示 */
    disabled?: boolean;
    /** 下拉项右侧灰色辅助文案（如“已发车”） */
    secondaryLabel?: string;
}

interface TransitInfoTableProps {
    dataSource: TransitInfoRow[];
    onChange: (rows: TransitInfoRow[]) => void;
    /** 转运站候选：本路线途径站+终到站（已发车站以 disabled+secondaryLabel 置灰不可选，不剔除） */
    transitStationOptions: TransitOption[];
    /** 转运目的地站候选：HUB list（已剔除本路线站点） */
    transitDestStationOptions: TransitOption[];
    readOnly?: boolean;
}

/**
 * 转运信息表格（参考 BagInfoTable）：每行 = 转运目的地站 / 转运站，支持多行增删，加号/减号在「操作」列。
 * 转运目的地站全局唯一：已被其它行选中的从本行下拉过滤掉。
 */
const TransitInfoTable: React.FC<TransitInfoTableProps> = ({
    dataSource,
    onChange,
    transitStationOptions,
    transitDestStationOptions,
    readOnly = false,
}) => {
    const updateRow = useCallback(
        (key: string, update: Partial<TransitInfoRow>) => {
            onChange(dataSource.map((row) => (row.key === key ? { ...row, ...update } : row)));
        },
        [dataSource, onChange]
    );

    const handleDelete = useCallback(
        (row: TransitInfoRow) => {
            // 冻结行(转运货物已卸车)不可删除：先判，因 destUnloaded ⊆ destLoaded
            if (row.destUnloaded) {
                message.warning(String(t('tms.task.drawer.transitTable.destUnloadedCannotDelete')));
                return;
            }
            // 锁定行(转运目的地已装车)不可删除：点减号只提示，不真删
            if (row.destLoaded) {
                message.warning(String(t('tms.task.drawer.transitTable.destLoadedCannotDelete')));
                return;
            }
            onChange(dataSource.filter((r) => r.key !== row.key));
        },
        [dataSource, onChange]
    );

    const handleAddRow = useCallback(() => {
        const newRow: TransitInfoRow = {
            key: `transit-${Date.now()}`,
            transitWarehouseId: '',
            transitDestWarehouseId: '',
        };
        onChange([...dataSource, newRow]);
    }, [dataSource, onChange]);

    const addButton = (
        <Tooltip title={String(t('tms.task.drawer.transitTable.add'))} color="#555" mouseEnterDelay={1}>
            <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddRow}
                style={{ color: '#666', transition: 'all 0.3s ease' }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3e5ff';
                    e.currentTarget.style.color = '#722ed1';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#666';
                }}
            />
        </Tooltip>
    );

    // 只读态把 value 解析成展示文案（DFW / MIA 等）
    const labelOf = (options: TransitOption[], value: string): string =>
        options.find((o) => String(o.value) === String(value))?.label ?? value ?? '';

    // 转运目的地站全局唯一：已被其他行选中的目的地，从本行下拉里直接过滤掉
    const destStationOptionsForRow = (row: TransitInfoRow): TransitOption[] => {
        const takenByOthers = new Set(
            dataSource
                .filter((r) => r.key !== row.key && r.transitDestWarehouseId)
                .map((r) => r.transitDestWarehouseId)
        );
        return transitDestStationOptions.filter((o) => !takenByOthers.has(String(o.value)));
    };

    const renderRow = (row: TransitInfoRow, index: number) => (
        <tr key={row.key} className="bg-transparent" style={{ height: '32px' }}>
            {readOnly ? (
                <>
                    {/* 查看详情：纯文本单元格，无边框/分隔线 */}
                    <td className="px-2 py-1 text-sm text-gray-800 text-center">
                        {labelOf(transitDestStationOptions, row.transitDestWarehouseId) && (
                            <span className="inline-block bg-gray-100 rounded-full px-3 py-0.5">
                                {labelOf(transitDestStationOptions, row.transitDestWarehouseId)}
                            </span>
                        )}
                    </td>
                    <td className="px-2 py-1 text-sm text-gray-800 text-center">
                        {labelOf(transitStationOptions, row.transitWarehouseId) && (
                            <span className="inline-block bg-gray-100 rounded-full px-3 py-0.5">
                                {labelOf(transitStationOptions, row.transitWarehouseId)}
                            </span>
                        )}
                    </td>
                </>
            ) : (
                <>
                    <td className="px-2 py-1 text-center">
                        <SingleSelect
                            options={destStationOptionsForRow(row)}
                            value={row.transitDestWarehouseId}
                            onChange={(value) => updateRow(row.key, { transitDestWarehouseId: value })}
                            placeholder={String(t('common.pleaseSelect'))}
                            disabled={readOnly || !!row.destLoaded}
                            allowClear={!row.destLoaded}
                        />
                    </td>
                    <td className="px-2 py-1 text-center">
                        <SingleSelect
                            options={transitStationOptions}
                            value={row.transitWarehouseId}
                            onChange={(value) => updateRow(row.key, { transitWarehouseId: value })}
                            placeholder={String(t('common.pleaseSelect'))}
                            disabled={readOnly || !!row.destUnloaded}
                            allowClear={!row.destUnloaded}
                        />
                    </td>
                </>
            )}
            {!readOnly && (
                <td className="px-2 py-1">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {/* 加号只放最后一行；其余行只留减号 */}
                        {index === dataSource.length - 1 && addButton}
                        {/* 至少保留一行：只剩一行时不显示减号，不允许删到 0 行。锁定行(已装车)减号照常显示，点了弹提示不真删 */}
                        {dataSource.length > 1 && (
                            <Tooltip title={String(t('tms.task.drawer.transitTable.delete'))} color="#555" mouseEnterDelay={1}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<MinusOutlined />}
                                    onClick={() => handleDelete(row)}
                                    style={{ color: '#666', transition: 'all 0.3s ease' }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#ffe6e6';
                                        e.currentTarget.style.color = '#ff4d4f';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#666';
                                    }}
                                />
                            </Tooltip>
                        )}
                    </div>
                </td>
            )}
        </tr>
    );

    return (
        <div
            className="mb-4"
            style={{
                // 固定宽：查看态 220px（两列各 110）；编辑态 460px（含操作列）。
                // 编辑态加宽：转运站 option 带 secondaryLabel，宽度不够会把 label 挤成省略号。
                // 不再对齐上方表单字段（那需要按 grid-cols-3 反推列宽），需调整改这里的 px 即可。
                width: readOnly ? '280px' : '430px',
                backgroundColor: 'transparent',
            }}
        >
            <div className="overflow-hidden rounded-lg">
                <table
                    className={`text-left ${readOnly ? 'border-collapse' : ''}`}
                    style={{ width: '100%', tableLayout: 'fixed' }}
                >
                    <thead>
                        <tr style={{ height: '32px' }}>
                            <th className="px-2 py-1 text-sm font-normal text-gray-500 text-center">
                                {String(t('tms.task.drawer.transitTable.transitDest'))}
                                {!readOnly && <span className="text-red-500 ml-1">*</span>}
                            </th>
                            <th className="px-2 py-1 text-sm font-normal text-gray-500 text-center">
                                {String(t('tms.task.drawer.transitTable.transitStation'))}
                                {!readOnly && <span className="text-red-500 ml-1">*</span>}
                            </th>
                            {!readOnly && (
                                <th className="px-2 py-1" style={{ width: '90px' }} />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {dataSource.length === 0 ? (
                            <tr className="bg-transparent" style={{ height: '32px' }}>
                                <td colSpan={2} className="px-2 py-1 text-center text-gray-500">
                                    {String(t('components.input.noData'))}
                                </td>
                                {!readOnly && (
                                    <td className="px-2 py-1">
                                        {addButton}
                                    </td>
                                )}
                            </tr>
                        ) : (
                            dataSource.map((row, index) => renderRow(row, index))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TransitInfoTable;
